import { useEffect, useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { format } from "date-fns";
import { useDebounce } from "use-debounce";

import { useAppDispatch, useAppSelector } from "../../../hooks";
import { selectSelectedBrandState, selectBrandState } from "../../../store/selectors/brands";
import {
  selectSourcePageChart,
  selectOverviewSourceUsageShowSkeleton,
  selectOverviewDomainsError,
  selectSourceDomainsPageRows,
  selectSourceDomainsPageTotal,
  selectSourceDomainsPageState,
  selectSelectedModelIdState,
  selectSourceDomainsChartDataLoading,
} from "../../../store/selectors/overview";
import { selectLastUpdateTimeByViewBrandIdState } from "../../../store/selectors/scores";
import { setViewBrandId } from "../../../store/brands/slice";
import {
  setSourceDomainsPage,
  fetchSourceDomainsChartData,
  setSourceDomainsChartData,
} from "../../../store/overview/slice";
import { useOverviewDateFilterFromUrl } from "../../Overview/hooks/useOverviewDateFilterFromUrl";
import { useOverviewDataFetch } from "../../Overview/hooks/useOverviewDataFetch";
import { DateSelectionMode, OverviewPeriod } from "../../../components/DatePeriodSelector/types";
import { exportDomainsToExcel } from "../utils/exportDomainsToExcel";
import type { SourcePageData } from "./types";

const SEARCH_DEBOUNCE_MS = 300;

export function useSourcePage(): SourcePageData {
  const dispatch = useDispatch();
  const appDispatch = useAppDispatch();
  const { dateFilter, setDateFilterFromUrl } = useOverviewDateFilterFromUrl();
  useOverviewDataFetch();

  const selectedBrand = useAppSelector(selectSelectedBrandState);
  const mainBrand = useAppSelector(selectBrandState);
  const selectedModelId = useAppSelector(selectSelectedModelIdState);
  const chart = useAppSelector(selectSourcePageChart);
  const showSourceUsageSkeleton = useAppSelector(selectOverviewSourceUsageShowSkeleton);
  const sourceDomainsChartDataLoading = useAppSelector(selectSourceDomainsChartDataLoading);
  const sourceDomainsPage = useAppSelector(selectSourceDomainsPageState);
  const domainsPageRows = useAppSelector(selectSourceDomainsPageRows);
  const domainsPageTotal = useAppSelector(selectSourceDomainsPageTotal);

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch] = useDebounce(searchInput, SEARCH_DEBOUNCE_MS);

  const hasChartData = chart.data !== undefined && chart.series !== undefined;
  const searchActive = debouncedSearch.trim().length > 0;
  const showChartSkeleton =
    showSourceUsageSkeleton || (searchActive && sourceDomainsChartDataLoading);
  const tableLoading = searchActive
    ? sourceDomainsChartDataLoading
    : showSourceUsageSkeleton;
  const overviewError = useAppSelector(selectOverviewDomainsError);
  const tableError = searchActive ? null : overviewError;

  useEffect(() => {
    if (mainBrand && selectedBrand == null) {
      dispatch(setViewBrandId(mainBrand.id));
    }
  }, [mainBrand, selectedBrand, dispatch]);

  useEffect(() => {
    if (selectedBrand == null) return;
    appDispatch(setSourceDomainsPage({ page: 1 }));
  }, [dateFilter, selectedBrand, debouncedSearch, appDispatch]);

  useEffect(() => {
    if (selectedBrand == null) return;
    const searchParam = debouncedSearch.trim();
    if (searchParam.length > 0) {
      const modelIdPayload = selectedModelId != null ? { model_id: selectedModelId } : {};
      const payload = { ...modelIdPayload, search: searchParam };
      if (dateFilter.type === DateSelectionMode.Date) {
        appDispatch(
          fetchSourceDomainsChartData({
            ...payload,
            date: format(dateFilter.dateTs, "yyyy-MM-dd"),
          })
        );
      } else {
        const period: 7 | 30 =
          dateFilter.period === OverviewPeriod.ThirtyDays ? 30 : 7;
        appDispatch(fetchSourceDomainsChartData({ ...payload, period }));
      }
    } else {
      appDispatch(setSourceDomainsChartData(null));
    }
  }, [dateFilter, selectedBrand, selectedModelId, debouncedSearch, appDispatch]);

  const handleDomainsPageChange = useCallback(
    (page: number) => {
      appDispatch(setSourceDomainsPage({ page }));
    },
    [appDispatch]
  );

  const handleExportToExcel = useCallback(() => {
    exportDomainsToExcel(domainsPageRows);
  }, [domainsPageRows]);

  const lastUpdateTime = useAppSelector(selectLastUpdateTimeByViewBrandIdState);

  return {
    dateFilter,
    setDateFilterFromUrl,
    lastUpdateTime,
    chart,
    showChartSkeleton,
    hasChartData,
    sourceDomainsPage,
    tableLoading,
    tableError,
    domainsPageRows,
    domainsPageTotal,
    searchInput,
    setSearchInput,
    handleDomainsPageChange,
    handleExportToExcel,
  };
}
